import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTable, useSortBy, Column } from "react-table";
import {
  Visibility,
  Delete,
  MoreVert,
  UnfoldMore,
  KeyboardArrowUp,
  KeyboardArrowDown,
  CheckCircle
} from "@mui/icons-material";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingOutlineIcon from "@mui/icons-material/PendingOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import { ConfirmationModal } from "common/components";
import { RootState } from "common/redux/core/root.reducer";
import "./SortingTable.scss";

interface UploadedBy {
  initial: string;
  name: string;
  color: string;
}

interface DocumentData {
  id: string | number;
  documentGuid?: string;
  fileName: string;
  flowName?: string;
  docType?: string;
  documentType?: string;
  dataExtracted?: string;
  uploadedDate: string;
  uploadedBy?: UploadedBy;
  updatedDate?: string;
  activityStatus?: string;
  status?: string;
  validation_status?: string;
}

interface DocumentTableProps {
  data?: DocumentData[];
  onView?: (id: string | number) => void;
  onDelete?: (id: string | number, documentGuid?: string) => void;
  onEdit?: (id: string | number) => void;
  onApproveView?: (id: string | number) => void;
  activeTab?: string;
  isLoading?: boolean;
  error?: any;
  onRefresh?: () => void;
  alwaysEnableEdit?: boolean;
}

export default function DocumentTable({
  data = [],
  onView,
  onDelete,
  onEdit,
  onApproveView,
  activeTab,
  onRefresh,
  isLoading = false,
  error = null,
  alwaysEnableEdit = false
}: DocumentTableProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get activeTab from URL if not provided as prop
  const currentActiveTab = activeTab || searchParams.get("activeTab") || "";

  // Get current user information from Redux store
  const currentUser = useSelector((state: RootState) => state.user);

  // State for delete confirmation modal
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    documentId: string | number | null;
    documentName: string;
    documentGuid?: string;
    isDeleting: boolean;
  }>({
    isOpen: false,
    documentId: null,
    documentName: "",
    documentGuid: undefined,
    isDeleting: false
  });

  // Generate consistent color based on name
  const generateColorFromName = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Generate a bright, readable color
    const hue = Math.abs(hash) % 360;
    const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
    const lightness = 45 + (Math.abs(hash) % 20); // 45-65%

    // Convert HSL to hex
    const hslToHex = (h: number, s: number, l: number): string => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    return hslToHex(hue, saturation, lightness);
  };

  const tableData = useMemo(() => (data.length > 0 ? data : []), [data]);

  // Check if any record has a non-empty validation_status (excluding empty strings and "NA")
  const hasValidationStatus = useMemo(() => {
    const result = tableData.some(
      (item) =>
        item.validation_status &&
        item.validation_status.trim() !== "" &&
        item.validation_status.trim().toLowerCase() !== "na"
    );
    return result;
  }, [tableData]);

  // Helper functions for delete confirmation
  const handleDeleteClick = (
    documentId: string | number,
    documentName: string,
    documentGuid?: string
  ) => {
    setDeleteConfirmation({
      isOpen: true,
      documentId,
      documentName,
      documentGuid,
      isDeleting: false
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      documentId: null,
      documentName: "",
      documentGuid: undefined,
      isDeleting: false
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.documentId) return;

    setDeleteConfirmation((prev) => ({ ...prev, isDeleting: true }));

    try {
      await onDelete?.(
        deleteConfirmation.documentId!,
        deleteConfirmation.documentGuid
      );

      // Show success message
      ToastMessageUtil.show(
        `Document "${deleteConfirmation.documentName}" has been deleted successfully`,
        ToastVariant.SUCCESS
      );

      // Close the modal
      setDeleteConfirmation({
        isOpen: false,
        documentId: null,
        documentName: "",
        documentGuid: undefined,
        isDeleting: false
      });

      // Auto-refresh after successful deletion
      if (onRefresh) {
        setTimeout(async () => {
          try {
            await onRefresh();
          } catch (refreshError) {
            console.error("Error during refresh:", refreshError);
          }
        }, 1000);
      }
    } catch (deleteError) {
      console.error("Error deleting document:", deleteError);

      // Show error message
      ToastMessageUtil.show(
        `Failed to delete document "${deleteConfirmation.documentName}". Please try again.`,
        ToastVariant.ERROR
      );

      // Reset deleting state but keep modal open
      setDeleteConfirmation((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const columns = useMemo<Column<DocumentData>[]>(() => {
    const baseColumns: Column<DocumentData>[] = [
      {
        Header: "File name",
        accessor: "fileName"
      },
      {
        Header: "Document type",
        accessor: "documentType",
        Cell: ({ value }: { value: string | undefined }) => (
          <span>{value || "—"}</span>
        )
      },
      {
        Header: "Uploaded date",
        accessor: "uploadedDate"
      },
      {
        Header: "Uploaded by",
        accessor: "uploadedBy",
        Cell: ({ value }: { value: UploadedBy | undefined }) => {
          // If no uploaded by value, use current user information
          if (!value) {
            const displayName = currentUser.displayName || "Current User";
            const initial = displayName.charAt(0).toUpperCase();
            const color = generateColorFromName(displayName);

            return (
              <div className="uploaded-by">
                <div className="avatar" style={{ backgroundColor: color }}>
                  {initial}
                </div>
                <span>{displayName}</span>
              </div>
            );
          }

          return (
            <div className="uploaded-by">
              <div className="avatar" style={{ backgroundColor: value.color }}>
                {value.initial}
              </div>
              <span>{value.name}</span>
            </div>
          );
        }
      },
      {
        Header: "Extraction status",
        accessor: "status",
        Cell: ({ value }: { value: string | undefined }) => {
          const isFailed =
            value?.toLowerCase().includes("failed") ||
            value?.toLowerCase().includes("error");
          const isInProgress = value?.toLowerCase().includes("progress");
          const isQueued = value?.toLowerCase().includes("queued");

          return (
            <div className="status">
              {isInProgress ? (
                <HourglassBottomOutlinedIcon
                  className="check-icon"
                  style={{ color: "#f59e0b" }}
                />
              ) : isFailed ? (
                <FiberManualRecordIcon
                  className="check-icon"
                  style={{ color: "red" }}
                />
              ) : isQueued ? (
                <FiberManualRecordIcon
                  className="check-icon"
                  style={{ color: "#ff9500" }}
                />
              ) : (
                <CheckCircleOutlined className="check-icon" />
              )}
              <span>{value || "Unknown"}</span>
            </div>
          );
        }
      }
    ];

    baseColumns.push({
      Header: "Actions",
      id: "actions",
      Cell: ({ row }: { row: { original: DocumentData } }) => {
        const statusText = row.original.status || "";
        // Normalize to ignore spacing/casing between raw ("KnowledgeGraphGenerated")
        // and formatted ("Knowledge Graph Generated") status values.
        const normalizedStatus = statusText.toLowerCase().replace(/\s+/g, "");

        const disabledStatuses = [
          "Extraction Queued",
          "Extraction in Progress",
          "Extraction Error"
        ];

        const canViewDocument = !disabledStatuses.some((disabledStatus) =>
          statusText.toLowerCase().includes(disabledStatus.toLowerCase())
        );

        // The extraction -> knowledge-graph flow applies only to Engineering Drawings.
        const isEngineeringDrawing = (row.original.documentType || "")
          .toLowerCase()
          .includes("drawing");
        // Engineering Documents have no relationship flow, so Edit is disabled.
        const isEngineeringDocument = (row.original.documentType || "")
          .toLowerCase()
          .includes("document");
        // Once the knowledge graph is generated: edit is turned off, view opens it.
        const isKnowledgeGraphGenerated =
          isEngineeringDrawing && normalizedStatus.includes("knowledgegraph");
        // States where the graph isn't generated yet but editing is allowed
        // (extraction completed / review completed): only edit is active, view is off.
        const isPreGraphEditable =
          isEngineeringDrawing &&
          (normalizedStatus.includes("extractioncompleted") ||
            normalizedStatus.includes("reviewcompleted")) &&
          !isKnowledgeGraphGenerated;
        // Eye is enabled only after the graph is generated (or for other viewable states).
        const canViewEye =
          isKnowledgeGraphGenerated || (!isPreGraphEditable && canViewDocument);

        return (
          <div className="actions">
            {currentActiveTab === "approval" ? (
              <button
                className="icon-btn"
                disabled={!canViewDocument}
                onClick={() => {
                  if (onApproveView && canViewDocument) {
                    onApproveView(row.original.id);
                  } else if (canViewDocument) {
                    const documentGuid =
                      row.original.documentGuid || row.original.id;
                    navigate(`/documents/${documentGuid}?page=1&size=10`);
                  }
                }}
              >
                <VisibilityOutlined />
              </button>
            ) : (
              <button
                className="icon-btn"
                disabled={!canViewEye}
                onClick={() => {
                  if (!canViewEye) return;
                  const documentGuid =
                    row.original.documentGuid || row.original.id;
                  if (isKnowledgeGraphGenerated) {
                    navigate(`/drawing-intelligence/${documentGuid}`, {
                      state: { path: "/processing-hub" }
                    });
                  } else if (onView) {
                    onView(row.original.id);
                  } else {
                    navigate(`/documents/${documentGuid}?page=1&size=10`);
                  }
                }}
              >
                <VisibilityOutlined />
              </button>
            )}
            {currentActiveTab !== "approval" &&
              currentActiveTab !== "completed" && (
                <>
                  <button
                    className="icon-btn"
                    disabled={
                      isEngineeringDocument ||
                      isKnowledgeGraphGenerated ||
                      (!alwaysEnableEdit && !canViewDocument)
                    }
                    onClick={() => {
                      if (
                        onEdit &&
                        !isEngineeringDocument &&
                        !isKnowledgeGraphGenerated &&
                        (alwaysEnableEdit || canViewDocument)
                      ) {
                        onEdit(row.original.id);
                      }
                    }}
                  >
                    <EditOutlined />
                  </button>
                  <button
                    className="icon-btn"
                    // disabled={!isExtractionCompleted}
                    onClick={() => {
                      const documentGuid =
                        row.original.documentGuid || row.original.id;
                      handleDeleteClick(
                        row.original.id,
                        row.original.fileName,
                        String(documentGuid)
                      );
                    }}
                  >
                    <DeleteOutlined />
                  </button>
                </>
              )}
          </div>
        );
      }
    });

    return baseColumns;
  }, [
    onView,
    onDelete,
    onEdit,
    onApproveView,
    currentUser,
    navigate,
    hasValidationStatus
  ]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<DocumentData>(
      {
        columns,
        data: tableData
      },
      useSortBy
    );

  return (
    <>
      <div className="table-container">
        <table {...(getTableProps() as any)} className="document-table">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps() as any;
              return (
                <tr {...headerGroupProps} key={headerGroupKey}>
                  {headerGroup.headers.map((column: any) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id}
                      className={column.canSort ? "sortable" : ""}
                    >
                      <div className="header-content">
                        {column.render("Header")}
                        {column.canSort && (
                          <span className="sort-icon">
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <KeyboardArrowDown />
                              ) : (
                                <KeyboardArrowUp />
                              )
                            ) : (
                              <UnfoldMore />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              );
            })}
          </thead>
          <tbody {...(getTableBodyProps() as any)}>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="no-data-message">
                  No data found
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...(row.getRowProps() as any)} key={row.id}>
                    {row.cells.map((cell) => (
                      <td
                        {...(cell.getCellProps() as any)}
                        key={cell.column.id}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteConfirmation.documentName}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={deleteConfirmation.isDeleting}
      />
    </>
  );
}
